import "./PrivacyAndSecurity.scss";
import { useEffect, useState } from "react";
import Container from "@material-ui/core/Container";
import zeroTrustImage from "../../Assets/Images/zero-trust.svg";
import e2eeImage from "../../Assets/Images/zero-trust.svg";
import secure from "../../Assets/Images/SECURE 25Jun2021.png";
import fontawesome from "@fortawesome/fontawesome";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLongArrowAltLeft,
  faLongArrowAltRight,
} from "@fortawesome/free-solid-svg-icons";

const PrivacyAndSecurity = (props) => {
  fontawesome.library.add(faLongArrowAltLeft, faLongArrowAltRight);
  window.executeScroll = props.executeScroll;
  // Content for the container based on above defined states
  const e2ee = {
    short:
      "End-to-end encryption for most of the secure applications protects in-transit data from the sender’s device to the recipient’s device only; it does not provide the same level of protection for any data stored on either of the sender’s or recipient’s devices. In contrast, Bayun’s true E2EE model protects data for its entire life-cycle, including data stored on the sender or recipient devices, and any copies of it (e.g. device backups).",
    long: "End-to-End Encryption (E2EE) is a technique employed by a select number of highly secure applications (e.g., Signal, WhatsApp) to ensure that user data is encrypted directly between the sender's and recipient's devices. This contrasts with hop-by-hop encryption, commonly used by most systems, where the encryption key for each hop is controlled by the domain owner of that hop. This remains true even for systems that utilize industry best practices for Data at Rest (DAR) and Data in Transit (DIT) encryption. With E2EE, the application's communication infrastructure for transferring user data and its storage servers are unable to access any data.<br><br>However, in practice, what is often called E2EE is simply device-to-device encryption (between the sender's device and the recipient's device), and not truly end-to-end in the sense that data stored on the user's device(s) is still encrypted using traditional methods, with the encryption key belonging to the domain ownership of the end-device. Consequently, when this user data is moved or copied elsewhere (e.g., backups to iCloud or GDrive, even in the case of WhatsApp), traditional DAR and DIT encryption methods are employed for all such copies, thereby enabling these different domain owners to access the user data.<br><br>Bayun's innovative E2EE++ technique embeds access policies directly into the data encryption itself, ensuring that these policies accompany the data regardless of where it is transferred or stored. As a result, data stored locally on user devices, as well as any copies made during backup or transfer, remain encrypted with the original access policies. This prevents anyone else, including the owner of the domain where a particular copy of the data resides, from accessing it. Therefore, Bayun's encryption model implements user-to-user encryption and is truly E2EE, a method significantly superior to the traditional understanding of the term as used by some of the most secure applications available.",
    image: e2eeImage,
  };
  const zeroTrust = {
    short:
      "Zero Trust typically means not trusting a network the user/service is coming from in order to enforce access control on data. Bayun’s DigiLockbox takes the Zero Trust model to its ultimate extreme with trustless enforcement of policies at the data level, where even no “enforcer” is trusted either (to validate a “stranger”), thus relying on math of cryptography alone for access control. This ensures that even if all server-side infrastructure of an application, and its associated services (e.g. authentication system), were to be compromised and/or privileged admins were to become malicious, no damage can be done.",
    long: `Zero Trust was originally coined by Forrester as a term for an application architecture designed to not inherently trust the network a user is connecting from. This approach was intended to move beyond trusting the perimeter created by firewalls and VPNs, instead requiring every application or service to individually authenticate the user (e.g., using SSO) before granting any data access. Over time, the term expanded to encompass a broader scope, featuring increasingly narrower perimeters through micro-segmentation in the context of microservices and similar advancements. This expansion also extended identity verification to services, not just users.<br><br>The overall architecture shifts trust from the network boundary to the application or microservice boundary. Nevertheless, it still necessitates trust in the application/microservice itself to validate the identity of the connecting entity (possibly via a separate authentication service) before authorizing any data access. Consequently, all Zero Trust systems currently available (including those with Multi-Factor Authentication) rely on <u>trusted</u> server-side code within the authentication service, as well as some <u>trusted</u> authorization code within the application logic or another authorization system. These components act as "enforcers" to prevent unauthorized access to data by "strangers" simply by being within a designated secure perimeter. In fact, some systems even rely on multiple trusted servers and/or communication networks for authentication alone (e.g., OTP or magic-link delivery via email or SMS). Additionally, a few administrators with privileged access to these servers can always bypass access restrictions and therefore must be inherently trusted.<br><br>Bayun's model embeds authentication and authorization logic directly into data encryption itself. This allows arbitrary access policies to be enforced on data <u>without ever having to trust any server-side logic</u> (<span class="request-wallpaper-pointer" onClick={window.executeScroll()}>Request Bayun’s DigiLockbox Whitepaper</span> to learn more). These policies, which define an authorized set of users, groups, and/or services, can be modified by an authorized user even after data has been locked, without the need to re-encrypt the data. More importantly, it removes the need to trust any server infrastructure to act as an "enforcer" for access control. This means that even if all the servers in the world were to be hacked, compromised, or taken over by a malicious or rogue entity, a client-side application using the Bayun SDK cannot be fooled, and no damage is possible.<br><br>Bayun's unique <u>trustless</u> model enables the creation of a true ZeroTrust++ architecture, where the perimeter of trust has been shrunk to its ultimate extreme: the data-object level. Crucially, no server-side infrastructure or code needs to be trusted by a user when accessing any data, let alone a privileged administrator. This represents the strongest technically feasible level of data privacy and security. As a result, software vulnerabilities invariably exploited by hackers in server-side logic (including, but not limited to, authentication or authorization systems) or via the backdoor access of privileged administrators to gain unauthorized access to user data are rendered useless. This is because authorization on data is now enforced through the mathematics of cryptography in a truly trustless manner, rather than requiring trust in any code or privileged administrators.`,
    image: zeroTrustImage,
  };

  // pageActive can be 1 or 2
  // 1: e2ee, 2: zero trust
  const [pageContent, setPageContent] = useState({
    pageActive: props.selectedTab,
    content: props.selectedTab === 2 ? zeroTrust : e2ee,
    zeroTrustClasses: props.selectedTab === 2 ? "tab active" : "tab",
    e2eeClasses: props.selectedTab === 2 ? "tab" : "tab active",
  });

  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    showPage(props.selectedTab);
  }, [props.selectedTab]);

  const showPage = (pageIndex) => {
    let pageData;

    if (pageIndex === 1) {
      pageData = {
        pageActive: 1,
        content: e2ee,
        zeroTrustClasses: "tab",
        e2eeClasses: "tab active",
      };
    } else {
      pageData = {
        pageActive: 2,
        content: zeroTrust,
        zeroTrustClasses: "tab active",
        e2eeClasses: "tab",
      };
    }
    setPageContent(pageData);
  };

  return (
    <div className="privacy-security-container">
      <Container>
        <div
          className="privacy-security"
          ref={(ref) => props.assignZeroTrustRef(ref)}
        >
          <div className="heading">
            <img src={secure} alt="Security" />
            <p>Privacy &amp; Security</p>
          </div>
          <div className="tab-group">
            <div
              className={pageContent.e2eeClasses}
              onClick={() => showPage(1)}
            >
              <span>E2EE ++</span>
            </div>
            <div
              className={pageContent.zeroTrustClasses}
              onClick={() => showPage(2)}
            >
              <span>ZeroTrust ++</span>
            </div>
          </div>
          <div className="content">
            <div className="image">
              <img src={pageContent.content.image} alt="Privacy And Security" />
            </div>
            {expanded ? (
              <div className="text">
                <p
                  dangerouslySetInnerHTML={{ __html: pageContent.content.long }}
                ></p>
                <div className="button">
                  <span onClick={() => setExpanded(false)}>
                    Show Less{" "}
                    <FontAwesomeIcon
                      icon="long-arrow-alt-left"
                      className="icon"
                    />
                  </span>
                </div>
              </div>
            ) : (
              <div className="text">
                {/* dangerouslySetInnerHTML={{__html:pageContent.content.short}}> */}
                <p>{pageContent.content.short}</p>
                <div className="button">
                  <span onClick={() => setExpanded(true)}>
                    Learn More{" "}
                    <FontAwesomeIcon
                      icon="long-arrow-alt-right"
                      className="icon"
                    />
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </Container>
    </div>
  );
};

export default PrivacyAndSecurity;
